import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '../../components/Button';
import { Field, inputStyle } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { useAuth } from '../../auth/AuthContext';
import { apiSend, ClientApiError } from '../../api/client';
import { MIN_PASSWORD_LENGTH } from '../../../shared/validation';

export function ChangePasswordScreen() {
  const { user, loading, signIn, signOut } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setBusy(true);
    try {
      await apiSend('/api/auth/change-password', 'POST', { currentPassword, newPassword });
      // Changing the password clears every session, so sign back in immediately.
      try {
        await signIn(user!.email, newPassword);
      } catch {
        // The password change itself succeeded, so the user's old session is already
        // dead and their old current password no longer works. Re-showing this form
        // would strand them. Clear local state and send them to login instead.
        try {
          await signOut();
        } catch {
          // Best-effort only: the server session is already gone, so a failed
          // sign-out call here must not block the redirect to login.
        }
        navigate('/login', { replace: true, state: { passwordChanged: true } });
        return;
      }
      navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ClientApiError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle={
        user.mustChangePassword
          ? 'You are signed in with a temporary password. Choose your own to continue.'
          : undefined
      }
    >
      <form onSubmit={submit} noValidate>
        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}
        <Field label="Current password" htmlFor="currentPassword">
          <input
            id="currentPassword"
            type="password"
            autoComplete="current-password"
            style={inputStyle}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </Field>
        <Field
          label="New password"
          htmlFor="newPassword"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        >
          <input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            style={inputStyle}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" full disabled={busy}>
          {busy ? 'Saving' : 'Save password'}
        </Button>
      </form>
    </AuthLayout>
  );
}
